/* iter55g25 — Apply Suggestion inline rewrite dialog.
 *
 * When the writer clicks "Apply" next to a Revision-Mode note, this
 * dialog opens with:
 *  1. A "Generate rewrite" button that asks Claude to rewrite the
 *     current selection (or the freeform result) using the note as
 *     guidance, streaming via SSE so it stays under Cloudflare's
 *     524 timeout window.
 *  2. An editable textarea so the writer can polish the rewrite.
 *  3. An "Apply to chapter" button that swaps the rewrite in for the
 *     selection (or appends it as a new paragraph if there's no
 *     selection).
 *
 * All actions call the project's existing SSE + REST endpoints — no
 * new backend routes required.
 */
import React, { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Sparkles, Save, X, PenLine } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { streamSSE } from '@/lib/auth_api';
import { chaptersApi } from '@/lib/api';

export default function ApplySuggestionDialog({
  projectId, chapterId, note, initialRewrite = '', selectionText = '',
  onCancel, onApplied,
}) {
  const [rewrite, setRewrite]   = useState(initialRewrite || '');
  const [streaming, setStreaming] = useState(false);
  const [saving, setSaving]     = useState(false);

  const generate = useCallback(async () => {
    if (!projectId || !chapterId) return;
    setStreaming(true);
    let buf = '';
    let finalText = '';
    try {
      // iter55g25 — dedicated rewrite SSE endpoint with proper
      // rewrite-oriented prompt semantics + Cloudflare keep-alive pings.
      await streamSSE(
        `/projects/${projectId}/stream/apply-suggestion`,
        {
          chapter_id: chapterId,
          note,
          selection_text: selectionText || null,
        },
        {
          onEvent: (ev) => {
            if (ev.event === 'delta') {
              buf += ev.data.text;
              setRewrite(buf);
            } else if (ev.event === 'done') {
              finalText = ev.data?.text || buf;
              if (finalText) setRewrite(finalText);
            } else if (ev.event === 'error') {
              toast.error(`Rewrite error: ${ev.data.message || 'unknown'}`);
            }
          },
        },
      );
    } catch (e) {
      toast.error(e?.message || 'Rewrite stream dropped.');
    } finally {
      setStreaming(false);
    }
  }, [projectId, chapterId, note, selectionText]);

  const apply = async () => {
    if (!chapterId || !rewrite.trim()) return;
    setSaving(true);
    try {
      const ch = await chaptersApi.get(chapterId);
      const original = ch?.content || '';
      let next;
      if (selectionText && original.includes(selectionText)) {
        next = original.replace(selectionText, rewrite.trim());
      } else {
        next = original.trim()
          ? `${original.trimEnd()}\n\n${rewrite.trim()}`
          : rewrite.trim();
      }
      await chaptersApi.update(chapterId, { content: next });
      await onApplied?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not save rewrite.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && !saving && !streaming && onCancel?.()}>
      <DialogContent
        className="flex max-h-[90vh] w-[min(90vw,42rem)] flex-col overflow-hidden"
        data-testid="apply-suggestion-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenLine className="h-4 w-4 text-primary" /> Apply suggestion
          </DialogTitle>
          <DialogDescription className="text-[13.5px]">
            Generate a rewrite of the highlighted passage from this note, edit
            it inline, then push it back into the chapter.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 overflow-y-auto p-1">
          <div className="rounded-sm border border-panel-border bg-panel-header/50 p-2 text-[13.5px]">
            <div className="mb-0.5 text-[13px] font-semibold uppercase tracking-wider text-muted-foreground">
              Editorial note
            </div>
            {note}
          </div>
          {selectionText && (
            <div className="rounded-sm border border-panel-border bg-background p-2 text-[13.5px] italic">
              <div className="mb-0.5 text-[13px] font-semibold uppercase tracking-wider not-italic text-muted-foreground">
                Highlighted passage
              </div>
              {selectionText.length > 400 ? `${selectionText.slice(0, 400)}…` : selectionText}
            </div>
          )}
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-[13px] font-semibold">Editable rewrite</label>
              <Button
                size="sm" variant="secondary"
                onClick={generate}
                disabled={streaming || saving}
                data-testid="apply-suggestion-generate"
                className="h-6 gap-1 text-[13px]"
              >
                {streaming ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                {rewrite ? 'Regenerate' : 'Generate rewrite'}
              </Button>
            </div>
            <textarea
              value={rewrite}
              onChange={(e) => setRewrite(e.target.value)}
              rows={10}
              data-testid="apply-suggestion-textarea"
              className="w-full resize-y rounded-sm border border-panel-border bg-background p-2 text-[13.5px] leading-relaxed outline-none focus:border-primary"
              placeholder="Rewrite will stream here. You can also type your own."
            />
          </div>
        </div>

        <DialogFooter className="flex flex-wrap gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={streaming || saving}
                  data-testid="apply-suggestion-cancel">
            <X className="mr-1 h-3.5 w-3.5" /> Cancel
          </Button>
          <Button
            onClick={apply}
            disabled={streaming || saving || !rewrite.trim()}
            data-testid="apply-suggestion-apply"
            className="gap-1"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Apply to chapter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
